import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useProgress } from '../context/ProgressContext';
import { CelebrationEffect } from './CelebrationEffect';
import { getStoryById, Story, StoryNode } from '../data/storiesData';
export function StoryPlayer({
  storyId,
  onExit
}: {
  storyId: string;
  onExit: () => void;
}) {
  const [currentNodeId, setCurrentNodeId] = useState('start');
  const [feedback, setFeedback] = useState<string | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const {
    addPoints,
    completeStory,
    earnBadge,
    progress
  } = useProgress();
  const userGender = progress.preferences.gender || 'prefer-not-to-say';
  const story = getStoryById(storyId, userGender);
  if (!story) {
    return <div className="min-h-screen w-full bg-gradient-to-br from-blue-900 via-indigo-900 to-slate-900 flex items-center justify-center p-4" data-id="element-248">
        <div className="max-w-2xl w-full bg-white dark:bg-slate-800 rounded-3xl shadow-2xl p-8 text-center" data-id="element-249">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-250">
            Story Not Found
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6" data-id="element-251">
            We couldn't find this story. Please try another one.
          </p>
          <button onClick={onExit} className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all" data-id="element-252">
            Back to Stories
          </button>
        </div>
      </div>;
  }
  const currentNode = story.nodes.find(node => node.id === currentNodeId);
  if (!currentNode) {
    return <div className="min-h-screen w-full bg-gradient-to-br from-blue-900 via-indigo-900 to-slate-900 flex items-center justify-center p-4" data-id="element-253">
        <div className="max-w-2xl w-full bg-white dark:bg-slate-800 rounded-3xl shadow-2xl p-8 text-center" data-id="element-254">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-255">
            Navigation Error
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6" data-id="element-256">
            Something went wrong with the story navigation. Let's start over!
          </p>
          <button onClick={() => {
          setCurrentNodeId('start');
          setFeedback(null);
        }} className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all mr-4" data-id="element-257">
            Restart Story
          </button>
          <button onClick={onExit} className="px-8 py-3 bg-gray-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all" data-id="element-258">
            Back to Stories
          </button>
        </div>
      </div>;
  }
  const handleChoice = (choice: {
    text: string;
    next: string;
    feedback?: string;
  }) => {
    if (choice.feedback) {
      setFeedback(choice.feedback);
      setTimeout(() => setFeedback(null), 3000);
    }
    setCurrentNodeId(choice.next);
  };
  const handleComplete = () => {
    if (currentNode.endingType === 'good') {
      addPoints(20);
      completeStory(storyId);
      setShowCelebration(true);
      if (storyId === 'consent-party') {
        earnBadge('consent-champion');
      }
    }
    setTimeout(() => {
      onExit();
    }, 3000);
  };
  if (currentNode.isEnding) {
    return <>
        <CelebrationEffect trigger={showCelebration && currentNode.endingType === 'good'} message="Story Complete!" points={20} data-id="element-259" />
        <div className="min-h-screen w-full bg-gradient-to-br from-blue-900 via-indigo-900 to-slate-900 flex items-center justify-center p-4" data-id="element-260">
          <div className="max-w-2xl w-full bg-white dark:bg-slate-800 rounded-3xl shadow-2xl p-8 text-center" data-id="element-261">
            <div className="text-6xl mb-4 animate-bounce" data-id="element-262">
              {currentNode.endingType === 'good' ? '🎉' : currentNode.endingType === 'neutral' ? '🤔' : '💭'}
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-263">
              Story Complete!
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-6" data-id="element-264">
              You've completed "{story.title}"
            </p>
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-6 mb-6" data-id="element-265">
              <p className="text-gray-800 dark:text-gray-200 leading-relaxed mb-4" data-id="element-266">
                {currentNode.text}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-267">
                💡 Continue your learning journey by exploring more stories and
                quizzes!
              </p>
            </div>
            <button onClick={onExit} className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all" data-id="element-268">
              Back to Stories
            </button>
          </div>
        </div>
      </>;
  }
  return <div className="min-h-screen w-full bg-gradient-to-br from-blue-900 via-indigo-900 to-slate-900 flex items-center justify-center p-4" data-id="element-269">
      <div className="max-w-3xl w-full bg-white dark:bg-slate-800 rounded-3xl shadow-2xl overflow-hidden" data-id="element-270">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 flex items-center justify-between" data-id="element-271">
          <h2 className="text-white font-bold text-lg" data-id="element-272">{story.title}</h2>
          <button onClick={onExit} className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors" data-id="element-273">
            <X className="w-5 h-5 text-white" data-id="element-274" />
          </button>
        </div>
        {/* Story Content */}
        <div className="p-8" data-id="element-275">
          <div className="prose prose-lg mb-8" data-id="element-276">
            <p className="text-gray-800 dark:text-gray-200 leading-relaxed text-lg" data-id="element-277">
              {currentNode.text}
            </p>
          </div>
          {/* Feedback */}
          {feedback && <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500 rounded" data-id="element-278">
              <p className="text-sm text-blue-900 dark:text-blue-200" data-id="element-279">
                {feedback}
              </p>
            </div>}
          {/* Choices */}
          {currentNode.choices && <div className="space-y-3" data-id="element-280">
              {currentNode.choices.map((choice, index) => <button key={index} onClick={() => handleChoice(choice)} className="w-full text-left p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 hover:from-blue-100 hover:to-indigo-100 dark:hover:from-blue-900/30 dark:hover:to-indigo-900/30 rounded-xl border-2 border-blue-200 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-500 transition-all" data-id="element-281">
                  <span className="font-medium text-gray-900 dark:text-white" data-id="element-282">
                    {choice.text}
                  </span>
                </button>)}
            </div>}
          {/* Ending */}
          {currentNode.isEnding && <div className="text-center" data-id="element-283">
              <div className={`inline-block px-6 py-3 rounded-full mb-6 ${currentNode.endingType === 'good' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' : currentNode.endingType === 'neutral' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'}`} data-id="element-284">
                <span className="font-semibold" data-id="element-285">
                  {currentNode.endingType === 'good' ? '🎉 Great Choice!' : currentNode.endingType === 'neutral' ? '🤔 Think About It' : '💭 Learning Moment'}
                </span>
              </div>
              <button onClick={handleComplete} className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all" data-id="element-286">
                Complete Story
              </button>
            </div>}
        </div>
      </div>
    </div>;
}